import { useState, useEffect, useRef } from 'react';
import { useOutletContext, Link, Outlet, useNavigate } from 'react-router-dom';
import { Car as SupabaseCar, supabase, FuelType, getRandomEmoji, Currency, CURRENCY_SYMBOLS } from '../lib/supabase';
import { Confetti } from '../components/ui/Confetti';
import { dataManager } from '../lib/dataManager';
import carBrandsData from '../data/carBrands.json';
import { generateUUID } from '../lib/uuid';
import { Save, Upload, History, Loader2, ChevronRight, Eye, Trash2, Edit, FileDown, Archive, ArchiveX, Rocket, MapPin } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_COLORS } from '../lib/icons';
import { uploadImage, deleteImage, UploadError } from '../lib/r2';
import { logAppError } from '../lib/logAppError';
import { NavigationBar } from '../components/ui/NavigationBar';
import { CarIconPicker } from '../components/cars/CarIconPicker';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { useTranslations } from '../i18n';
import { theme } from '../lib/theme';
import { CarExpenseTypesEditor } from '../components/cars/CarExpenseTypesEditor';
import { EmailProcessingCombined } from '../components/cars/EmailProcessingCombined';
import { CarAdminsSection } from '../components/cars/CarAdminsSection';
import defaultExpenseTypesData from '../data/defaultExpenseTypes.json';
import { AlertModal } from '../components/ui/AlertModal';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { usePreferences } from '../contexts/PreferencesContext';
import { getCarLimitStatus } from '../lib/carLimits';
import { normalizeExpenseTypeKey } from '../lib/expenseTypeHelpers';
import { trackUserActivity } from '../lib/activityTracking';
import { DateInput } from '../components/ui/DateInput';
import { FormLabel } from '../components/ui/FormLabel';
import { convertToUTC, getDateInTimezone } from '../lib/timeUtils';

const defaultKeys = Object.entries(defaultExpenseTypesData)
  .filter(([_, value]: [string, any]) => {
    // Exclude deprecated items
    if (value.is_deprecated === true) return false;
    // Exclude items explicitly marked as NOT default
    if (value.is_default === false) return false;
    return true;
  })
  .map(([key, _]) => key);

const COLOR_TO_HUE: Record<string, number> = {
  '#F97316': 25,   // Orange
  '#3B82F6': 215,  // Blue
  '#10B981': 160,  // Green
  '#8B5CF6': 260,  // Purple
  '#EF4444': 0,    // Red
  '#F59E0B': 38,   // Amber
  '#14B8A6': 175,  // Teal
  '#6366F1': 240,  // Indigo
  '#EC4899': 330,  // Pink
  '#6B7280': 999,  // Gray (special value)
  '#84CC16': 75,   // Lime
  '#06B6D4': 190,  // Cyan
};

const HUE_TO_COLOR: Record<number, string> = Object.fromEntries(
  Object.entries(COLOR_TO_HUE).map(([color, hue]) => [hue, color])
);

type CarFormProps = {
  onBack: () => void;
  onCarSaved: (car: SupabaseCar) => void;
  onCarDeleted?: () => void;
  editingCar?: SupabaseCar | null;
  onImportClick?: () => void;
  onDeleteAllClick?: () => void;
};

export function CarForm({ onBack, onCarSaved, onCarDeleted, editingCar, onImportClick, onDeleteAllClick }: CarFormProps) {
  const { user, isAdmin } = useAuth();
  const { t, language } = useTranslations();
  const navigate = useNavigate();
  const { preferences } = usePreferences();
  const outletContext = useOutletContext<any>() || {};
  const isAdminView = outletContext.isAdminView || false;
  const currentPlan = outletContext.ownerPreferences?.plan_type || 'free';
  const isRestricted = currentPlan === 'free' && !isAdminView;
  const isPro = !isRestricted;
  const effectiveUserId = outletContext.effectiveUserId || user?.id;
  /* const isArchived = !!(editingCar?.is_archived); */

  // Form State
  const [name, setName] = useState('');
  const [make, setMake] = useState('');
  const [makeInputFocused, setMakeInputFocused] = useState(false);
  const [filteredBrands, setFilteredBrands] = useState<typeof carBrandsData.brands>([]);
  const makeInputRef = useRef<HTMLInputElement>(null);
  const [model, setModel] = useState('');
  const [modelInputFocused, setModelInputFocused] = useState(false);
  const [filteredModels, setFilteredModels] = useState<string[]>([]);
  const modelInputRef = useRef<HTMLInputElement>(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [plateNumber, setPlateNumber] = useState('');
  const [vinNumber, setVinNumber] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [shareLocations, setShareLocations] = useState(false);
  const [fuelType, setFuelType] = useState<FuelType>('not_specified');
  const [boughtAt, setBoughtAt] = useState(editingCar?.bought_at ? new Date(editingCar.bought_at).toISOString().split('T')[0] : '');
  const [purchasePrice, setPurchasePrice] = useState<string>(editingCar?.purchase_price?.toString() || '');
  const [purchaseMileage, setPurchaseMileage] = useState<string>(editingCar?.purchase_mileage?.toString() || '');
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [carExpenseTypes, setCarExpenseTypes] = useState<string[]>([]);
  const [allowedEmails, setAllowedEmails] = useState<string[]>([]);
  // Icon / Visual State
  const initialHue = editingCar?.placeholder_hue ?? 200;
  const initialColor = HUE_TO_COLOR[initialHue] || DEFAULT_COLORS[0];
  const [placeholderColor, setPlaceholderColor] = useState(initialColor);
  const [iconText, setIconText] = useState(editingCar?.icon_text || getRandomEmoji()); // Stores Emoji
  const [useImageIcon, setUseImageIcon] = useState(editingCar?.use_image_icon || false);
  const [estimatedYearlyKm, setEstimatedYearlyKm] = useState<number | null>(editingCar?.estimated_yearly_km || null);
  const [summaryFrequency, setSummaryFrequency] = useState<string>(editingCar?.summary_frequency || 'none');
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string | null>(editingCar?.image_url || null);
  const [iconFile, setIconFile] = useState<File | null>(null); // For new uploads
  const [iconThumbFile, setIconThumbFile] = useState<File | null>(null); // For new thumbnail
  const [images] = useState<Array<{ url: string }>>([]);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [isIconPickerOpen, setIsIconPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ isOpen: boolean; title: string; message: string; secondaryAction?: { label: string; onClick: () => void } }>({ isOpen: false, title: '', message: '' });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Archive state
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [sellDate, setSellDate] = useState(editingCar?.sell_date ? getDateInTimezone(editingCar.sell_date, preferences?.timezone) : '');
  const [sellPrice, setSellPrice] = useState<string>(editingCar?.sell_price?.toString() || '');
  const [sellMileage, setSellMileage] = useState<string>(editingCar?.sell_mileage?.toString() || '');
  const [savingSellDetails, setSavingSellDetails] = useState(false);
  const [unitWarningShown, setUnitWarningShown] = useState(false);
  
  // V2.3 Metadata
  const initialIsUnknownBodyType = !!(editingCar?.body_type && !Object.keys(t.carMetadata.bodyTypes).includes(editingCar.body_type));
  const [bodyType, setBodyType] = useState(initialIsUnknownBodyType ? 'other' : (editingCar?.body_type || 'not_specified'));
  const [drivetrain, setDrivetrain] = useState(editingCar?.drivetrain || 'not_specified');
  const [transmission, setTransmission] = useState(editingCar?.transmission || 'not_specified');
  const [engineInfo, setEngineInfo] = useState(editingCar?.engine_info || '');
  const [customBodyType, setCustomBodyType] = useState(initialIsUnknownBodyType ? (editingCar?.body_type || '') : '');
  
  useEffect(() => {
    if (editingCar?.body_type && !Object.keys(t.carMetadata.bodyTypes).includes(editingCar.body_type)) {
      setBodyType('other');
      setCustomBodyType(editingCar.body_type);
    } else if (editingCar?.body_type) {
      setBodyType(editingCar.body_type);
      setCustomBodyType('');
    }
  }, [editingCar, t.carMetadata.bodyTypes]);

  // Restore scroll position
  useScrollRestoration(scrollContainerRef);

  const [carId] = useState(() => editingCar?.id || generateUUID());

  // Preferences State
  const [distanceUnit, setDistanceUnit] = useState<'km' | 'miles'>('km');
  const [volumeUnit, setVolumeUnit] = useState<'liters' | 'gallons'>('liters');
  const [currency, setCurrency] = useState<Currency>('EUR');

  // Initialize
  useEffect(() => {
    if (editingCar) {
      const hue = editingCar.placeholder_hue ?? 200;
      const color = HUE_TO_COLOR[hue] || DEFAULT_COLORS[0];

      const derivedIconText = editingCar.icon_text || getRandomEmoji();

      setName(editingCar.name || '');
      setMake(editingCar.make || '');
      setModel(editingCar.model || '');
      setYear(editingCar.year || new Date().getFullYear());
      setPlateNumber(editingCar.plate_number || '');
      setVinNumber(editingCar.vin_number || '');
      setIsPublic(editingCar.is_public || false);
      setShareLocations(editingCar.share_locations || false);
      setFuelType(editingCar.fuel_type || 'not_specified');
      setEstimatedYearlyKm(editingCar.estimated_yearly_km || null);
      setSummaryFrequency(editingCar.summary_frequency || 'none');

      // Initialize bought_at date
      if (editingCar.bought_at) {
        setBoughtAt(getDateInTimezone(editingCar.bought_at, preferences?.timezone));
      }

      setBrandLogoUrl(editingCar.brand_logo_url || null);

      // Load expense types
      if (editingCar.expense_types && editingCar.expense_types.length > 0) {
        setCarExpenseTypes(editingCar.expense_types.map(normalizeExpenseTypeKey));
      } else {
        setCarExpenseTypes(defaultKeys);
      }
      setAllowedEmails(editingCar.allowed_emails || []);

      // Load preferences
      setDistanceUnit(editingCar.distance_unit || 'km');
      setVolumeUnit(editingCar.volume_unit || 'liters');
      setCurrency(editingCar.currency || 'EUR');

      setPlaceholderColor(color);
      setUseImageIcon(editingCar.use_image_icon || false);
      setIconText(derivedIconText);

      if (editingCar.image_url) {
        setIconPreviewUrl(editingCar.image_url);
      }

      setPurchasePrice(editingCar.purchase_price?.toString() || '');
      setPurchaseMileage(editingCar.purchase_mileage?.toString() || '');
      setEngineInfo(editingCar.engine_info || '');

      const isUnknownBodyType = !!(editingCar.body_type && !Object.keys(t.carMetadata.bodyTypes).includes(editingCar.body_type));

      setInitialValues({
        name: editingCar.name || '',
        make: editingCar.make || '',
        model: editingCar.model || '',
        year: editingCar.year || new Date().getFullYear(),
        isPublic: editingCar.is_public || false,
        shareLocations: editingCar.share_locations || false,
        fuelType: editingCar.fuel_type || 'not_specified',
        plateNumber: editingCar.plate_number || '',
        vinNumber: editingCar.vin_number || '',
        placeholderColor: color,
        iconText: derivedIconText,
        useImageIcon: editingCar.use_image_icon || false,
        image_url: editingCar.image_url,
        gallery_count: 0,
        distanceUnit: editingCar.distance_unit || 'km',
        volumeUnit: editingCar.volume_unit || 'liters',
        currency: editingCar.currency || 'EUR',
        purchasePrice: editingCar.purchase_price?.toString() || '',
        purchaseMileage: editingCar.purchase_mileage?.toString() || '',
        expenseTypes: editingCar.expense_types && editingCar.expense_types.length > 0 ? editingCar.expense_types.map(normalizeExpenseTypeKey) : defaultKeys,
        allowedEmails: editingCar.allowed_emails || [],
        boughtAt: editingCar.bought_at ? getDateInTimezone(editingCar.bought_at, preferences?.timezone) : '',
        engineInfo: editingCar.engine_info || '',
        bodyType: isUnknownBodyType ? 'other' : (editingCar.body_type || 'not_specified'),
        customBodyType: isUnknownBodyType ? editingCar.body_type : '',
        drivetrain: editingCar.drivetrain || 'not_specified',
        transmission: editingCar.transmission || 'not_specified',
        estimatedYearlyKm: editingCar.estimated_yearly_km || null,
        summaryFrequency: editingCar.summary_frequency || 'none'
      });

      // Load initial purchase details from log_entries
      const loadPurchaseDetails = async () => {
        // Since we already used editingCar.purchase_price and purchase_mileage on state initialization, 
        // there is no strict need to query the database again unless they were missing on the car object.
        // If needed, we could fetch from log_entries directly here.
      };

      loadPurchaseDetails();
    } else {
      // Defaults for New Car
      const defaultColor = DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)];
      const defaultEmoji = getRandomEmoji();

      setPlaceholderColor(defaultColor);
      setIconText(defaultEmoji);

      // Default Expense Types
      setCarExpenseTypes(defaultKeys);
      setAllowedEmails([]);

      // Try to load defaults from first car
      const loadDefaults = async () => {
        if (user) {
          try {
            const cars = await dataManager.fetchCars(user.id);
            if (cars.length > 0) {
              // Use the first car's preferences as defaults
              const firstCar = cars[0];
              if (firstCar.distance_unit) setDistanceUnit(firstCar.distance_unit);
              if (firstCar.volume_unit) setVolumeUnit(firstCar.volume_unit);
              if (firstCar.currency) setCurrency(firstCar.currency);
              if (firstCar.estimated_yearly_km) setEstimatedYearlyKm(firstCar.estimated_yearly_km);
              if (firstCar.summary_frequency) setSummaryFrequency(firstCar.summary_frequency);
            }
          } catch (e) {
            console.error("Error loading defaults from previous cars", e);
          }
        }
      };
      loadDefaults();

      setInitialValues({
        name: '',
        make: '',
        model: '',
        year: new Date().getFullYear(),
        isPublic: false,
        shareLocations: false,
        fuelType: 'not_specified',
        plateNumber: '',
        vinNumber: '',
        placeholderColor: defaultColor,
        iconText: defaultEmoji,
        useImageIcon: false,
        image_url: null,
        gallery_count: 0,
        distanceUnit: 'km',
        volumeUnit: 'liters',
        currency: 'EUR',
        boughtAt: '',
        purchasePrice: '',
        purchaseMileage: '',
        expenseTypes: defaultKeys,
        allowedEmails: [],
        bodyType: 'not_specified',
        customBodyType: '',
        drivetrain: 'not_specified',
        transmission: 'not_specified'
      });
    }
  }, [editingCar, user]);

  const handleDeleteCall = async () => {
    if (!editingCar || !user) return;

    try {
      const { error } = await supabase.from('cars').delete().eq('id', editingCar.id);
      if (error) throw error;
      if (onCarDeleted) {
        onCarDeleted();
      } else {
        onBack();
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      setAlertConfig({ isOpen: true, title: t.common.error, message: t.cars.deleteFailed });
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleArchiveConfirm = async () => {
    if (!editingCar || !user) return;
    if (!sellDate || !sellPrice || !sellMileage) {
      setAlertConfig({ isOpen: true, title: t.common.error, message: (t.cars as any).archiveDetailsNotFillable || 'Please fill all fields to archive the car' });
      return;
    }
    setArchiving(true);
    try {
      const { data, error } = await supabase
        .from('cars')
        .update({
          is_archived: true,
          sell_date: new Date(sellDate + 'T12:00:00Z').toISOString(),
          sell_price: parseFloat(sellPrice) || 0,
          sell_mileage: parseInt(sellMileage) || 0,
        })
        .eq('id', editingCar.id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (data) {
        dataManager.invalidateCars(effectiveUserId);
        setShowArchiveModal(false);
        onCarSaved(data);
      }
    } catch (err) {
      console.error('Error archiving car:', err);
      setAlertConfig({ isOpen: true, title: t.common.error, message: t.cars.saveFailed });
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchive = async () => {
    if (!editingCar || !user) return;
    setArchiving(true);
    try {
      const { data, error } = await supabase
        .from('cars')
        .update({ is_archived: false })
        .eq('id', editingCar.id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (data) {
        dataManager.invalidateCars(effectiveUserId);
        onCarSaved(data);
      }
    } catch (err) {
      console.error('Error unarchiving car:', err);
      setAlertConfig({ isOpen: true, title: t.common.error, message: t.cars.saveFailed });
    } finally {
      setArchiving(false);
    }
  };

  const handleSaveSellDetails = async () => {
    if (!editingCar || !user) return;
    setSavingSellDetails(true);
    try {
      const { data, error } = await supabase
        .from('cars')
        .update({
          sell_date: sellDate ? new Date(sellDate + 'T12:00:00Z').toISOString() : null,
          sell_price: parseFloat(sellPrice) || null,
          sell_mileage: parseInt(sellMileage) || null,
        })
        .eq('id', editingCar.id)
        .select()
        .maybeSingle();
      if (error) throw error;
      if (data) {
        dataManager.invalidateCars(effectiveUserId);
        onCarSaved(data);
      }
    } catch (err) {
      console.error('Error saving sell details:', err);
      setAlertConfig({ isOpen: true, title: t.common.error, message: t.cars.saveFailed });
    } finally {
      setSavingSellDetails(false);
    }
  };

  const handleUnitChange = (setter: (val: any) => void, newVal: any, currentVal: any) => {
    if (editingCar && !unitWarningShown && newVal !== currentVal) {
      setAlertConfig({
        isOpen: true,
        title: (t.cars as any).unitChangeWarningTitle || 'Unit Change Warning',
        message: (t.cars as any).unitChangeWarningMessage || 'Changing these units will not convert your existing data. All historical records will remain exactly as entered.',
      });
      setUnitWarningShown(true);
    }
    setter(newVal);
  };

  const handleIconSave = (result: { type: 'emoji' | 'image'; value: string; color?: string; file?: File; thumbFile?: File }) => {
    if (result.type === 'emoji') {
      setUseImageIcon(false);
      setIconText(result.value);
      if (result.color) setPlaceholderColor(result.color);
    } else {
      setUseImageIcon(true);
      setIconPreviewUrl(result.value);
      if (result.file) setIconFile(result.file);
      if (result.thumbFile) setIconThumbFile(result.thumbFile);
    }
  };

  const handleSubmit = async (e?: React.FormEvent): Promise<boolean> => {
    if (e) e.preventDefault();
    if (!user || saving) return false;
    setSaving(true);

    try {
      if (!boughtAt) {
        setAlertConfig({ isOpen: true, title: t.common.error, message: t.cars.enterPurchaseDateAlert || 'Please enter purchase date' });
        setSaving(false);
        return false;
      }

      // --- CAR CREATION LIMIT CHECK (only for new cars) ---
      if (!editingCar) {
        const allCars = await dataManager.fetchCars(effectiveUserId);
        const limitStatus = getCarLimitStatus(allCars, preferences);
        if (limitStatus.isReached) {
          const planLabel = preferences?.plan_type === 'pro' ? (t.common.planProTier || 'Pro') : (t.common.planFreeTier || 'Free');
          const message = (t.cars.carLimitReachedMessage || '')
            .replace('{plan}', planLabel)
            .replace('{limit}', String(limitStatus.limit))
            .replace('{current}', String(limitStatus.current));
          // Log for admin tracking
          logAppError({
            source: 'frontend',
            statusCode: 429,
            path: '/cars/new',
            method: 'POST',
            message: `Car limit reached: plan=${preferences?.plan_type || 'free'}, limit=${limitStatus.limit}, current=${limitStatus.current}`,
            context: 'car_creation_limit',
          }).catch(() => {});
          setAlertConfig({
            isOpen: true,
            title: t.cars.carLimitReachedTitle || 'Car Limit Reached',
            message,
            secondaryAction: {
              label: t.cars.carLimitGoToSettings || 'Go to Settings',
              onClick: () => { onBack(); navigate('/settings'); },
            },
          });
          setSaving(false);
          return false;
        }
      }

      let finalImageUrl = editingCar?.image_url || '';
      let finalThumbUrl = editingCar?.image_url_thumb || '';

      // Handle Icon Image Upload
      if (useImageIcon && iconFile) {
        // Delete old image from R2 if it's being replaced
        if (editingCar?.image_url) {
          deleteImage(editingCar.image_url, editingCar.image_url_thumb || undefined).catch(() => { });
        }
        const result = await uploadImage(iconFile, 'car-icons', 'car', carId, iconThumbFile || undefined);
        finalImageUrl = result.imageUrl;
        finalThumbUrl = result.thumbUrl || result.imageUrl;
      }

      const hue = COLOR_TO_HUE[placeholderColor] ?? 200;

      const carData = {
        name,
        make,
        model,
        year,
        plate_number: plateNumber,
        vin_number: vinNumber,
        is_public: isPublic,
        share_locations: shareLocations,
        fuel_type: fuelType,
        placeholder_color: placeholderColor,
        placeholder_hue: hue,
        use_image_icon: useImageIcon,
        icon_text: iconText,
        user_id: editingCar ? editingCar.user_id : effectiveUserId,
        image_url: useImageIcon ? finalImageUrl : null,
        image_url_thumb: useImageIcon ? finalThumbUrl : null,
        distance_unit: distanceUnit,
        volume_unit: volumeUnit,
        currency: currency,
        currency_symbol: CURRENCY_SYMBOLS[currency],
        brand_logo_url: brandLogoUrl,
        bought_at: convertToUTC(boughtAt, '12:00', preferences?.timezone),
        expense_types: carExpenseTypes,
        purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
        purchase_mileage: purchaseMileage ? parseInt(purchaseMileage) : null,
        allowed_emails: allowedEmails,
        body_type: bodyType === 'other' ? customBodyType : bodyType,
        drivetrain,
        transmission,
        engine_info: engineInfo,
        estimated_yearly_km: estimatedYearlyKm,
        summary_frequency: summaryFrequency,
      };

      let finalPurchaseExpenseId = editingCar?.purchase_expense_id || null;

      if (!editingCar) {
        // --- NEW CAR FLOW ---
        // 1. Insert car first (without expense linked yet)
        const { data: carDataResult, error: carError } = await supabase
          .from('cars')
          .insert([{ ...carData, id: carId }])
          .select()
          .maybeSingle();

        if (carError) throw carError;

        if (carDataResult) {
          // 2. Create the LogEntry first
          const { data: newLog, error: logError } = await supabase
            .from('log_entries')
            .insert([{
              car_id: carId,
              user_id: effectiveUserId,
              date: carData.bought_at,
              mileage: carData.purchase_mileage,
              car_value: carData.purchase_price,
              title: '',
              is_initial_purchase_entry: true
            }])
            .select('id')
            .maybeSingle();

          if (logError) throw logError;

          if (newLog) {
            // 3. Create the purchase expense record linked to the log entry
            const { data: newExpense } = await supabase
              .from('expenses')
              .insert([{
                car_id: carId,
                user_id: effectiveUserId,
                log_entry_id: newLog.id,
                type: 'info',
                amount: carData.purchase_price,
                date: carData.bought_at,
                title: t.common.purchasedCarTitle,
                description: t.common.purchasedCarTitle,
                is_initial_purchase: true,
                created_at: new Date().toISOString()
              }])
              .select('id')
              .maybeSingle();

            if (newExpense) {
              finalPurchaseExpenseId = newExpense.id;
              // 4. Update car with the direct link
              await supabase
                .from('cars')
                .update({ purchase_expense_id: finalPurchaseExpenseId })
                .eq('id', carId);
            }
          }

          dataManager.invalidateGallery(carId);
          dataManager.invalidateLogEntries(carId);
          dataManager.invalidateCars(effectiveUserId);
          trackUserActivity('created_car', carId);
          
          onCarSaved({ ...carDataResult, purchase_expense_id: finalPurchaseExpenseId });
          return true;
        }
      } else {
        // --- EDITING CAR FLOW ---
        // Check if existing linked expense still exists and update it
        if (finalPurchaseExpenseId) {
          const { data: updatedExpense, error: updateError } = await supabase
            .from('expenses')
            .update({
              date: carData.bought_at,
              amount: carData.purchase_price,
              is_initial_purchase: true,
              description: t.common.purchasedCarTitle
            })
            .eq('id', finalPurchaseExpenseId)
            .select('id, log_entry_id')
            .maybeSingle();

          if (updateError || !updatedExpense) {
            finalPurchaseExpenseId = null; // Mark as null to trigger recreation/fallback if record is missing
          } else if (updatedExpense.log_entry_id) {
            // Also update the linked log entry
            const { error: logUpdateError } = await supabase
              .from('log_entries')
              .update({
                date: carData.bought_at,
                mileage: carData.purchase_mileage,
                car_value: carData.purchase_price,
                title: '',
                is_initial_purchase_entry: true
              })
              .eq('id', updatedExpense.log_entry_id);

            // If log entry was deleted but expense remained (unlikely but possible), try to recreate it
            if (logUpdateError) {
              const { data: newLog } = await supabase
                .from('log_entries')
                .insert([{
                  car_id: carId,
                  user_id: effectiveUserId,
                  date: carData.bought_at,
                  mileage: carData.purchase_mileage,
                  car_value: carData.purchase_price,
                  title: '',
                  is_initial_purchase_entry: true
                }])
                .select('id')
                .maybeSingle();

              if (newLog) {
                await supabase
                  .from('expenses')
                  .update({ log_entry_id: newLog.id })
                  .eq('id', updatedExpense.id);
              }
            }
          } else {
            // Log entry is missing (legacy record) -> create one and link it
            const { data: newLog } = await supabase
              .from('log_entries')
              .insert([{
                car_id: carId,
                user_id: effectiveUserId,
                date: carData.bought_at,
                mileage: carData.purchase_mileage,
                car_value: carData.purchase_price,
                title: '',
                is_initial_purchase_entry: true
              }])
              .select('id')
              .maybeSingle();

            if (newLog) {
              await supabase
                .from('expenses')
                .update({ log_entry_id: newLog.id })
                .eq('id', updatedExpense.id);
            }
          }
        }

        // If no ID or update failed, try legacy fallback (is_initial_purchase flag)
        if (!finalPurchaseExpenseId) {
          const { data: legacyLogEntry } = await supabase
            .from('log_entries')
            .select('id, expenses!inner(id)')
            .eq('car_id', carId)
            .eq('is_initial_purchase_entry', true)
            .maybeSingle();

          if (legacyLogEntry && (legacyLogEntry.expenses as any)?.length > 0) {
            const legacyExpense = (legacyLogEntry.expenses as any)[0];
            finalPurchaseExpenseId = legacyExpense.id;
            await supabase
              .from('expenses')
              .update({
                date: carData.bought_at,
                amount: carData.purchase_price,
                is_initial_purchase: true,
                description: t.common.purchasedCarTitle
              })
              .eq('id', legacyExpense.id);

            await supabase
              .from('log_entries')
              .update({
                date: carData.bought_at,
                mileage: carData.purchase_mileage,
                car_value: carData.purchase_price,
                title: '',
                is_initial_purchase_entry: true
              })
              .eq('id', legacyLogEntry.id);
          }
        }

        // If still no expense record exists (never created or deleted), create a new one
        if (!finalPurchaseExpenseId) {
          // Create LogEntry
          const { data: newLog } = await supabase
            .from('log_entries')
            .insert([{
              car_id: carId,
              user_id: effectiveUserId,
              date: carData.bought_at,
              mileage: carData.purchase_mileage,
              car_value: carData.purchase_price,
              title: '',
              is_initial_purchase_entry: true
            }])
            .select('id')
            .maybeSingle();

          if (newLog) {
            const { data: newExpense } = await supabase
              .from('expenses')
              .insert([{
                car_id: carId,
                user_id: effectiveUserId,
                log_entry_id: newLog.id,
                type: 'info',
                amount: carData.purchase_price,
                date: carData.bought_at,
                title: t.common.purchasedCarTitle,
                description: t.common.purchasedCarTitle,
                is_initial_purchase: true,
                created_at: new Date().toISOString()
              }])
              .select('id')
              .maybeSingle();

            if (newExpense) {
              finalPurchaseExpenseId = newExpense.id;
            }
          }
        }

        const finalCarData = {
          ...carData,
          purchase_expense_id: finalPurchaseExpenseId
        };

        const { data, error } = await supabase
          .from('cars')
          .update(finalCarData)
          .eq('id', carId)
          .select()
          .maybeSingle();

        if (error) throw error;

        if (data) {
          dataManager.invalidateGallery(editingCar.id);
          dataManager.invalidateLogEntries(editingCar.id);
          dataManager.invalidateCars(effectiveUserId);
          onCarSaved(data);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Error saving car:', error);
      let errorMessage = t.cars.saveFailed || 'Failed to save car data';
      if (error instanceof UploadError && error.code) {
        if (error.code === 'FILE_TOO_LARGE') {
          errorMessage = (t.common as any).fileTooLarge?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
          logAppError({ source: 'frontend', statusCode: 413, path: '/cars/upload', method: 'POST', message: `FILE_TOO_LARGE: limit=${error.limit}`, context: 'CarForm.upload' }).catch(() => {});
        } else if (error.code === 'STORAGE_FULL') {
          errorMessage = (t.common as any).storageFull?.replace('{limit}', error.limit?.toString() || '0') || errorMessage;
          logAppError({ source: 'frontend', statusCode: 429, path: '/cars/upload', method: 'POST', message: `STORAGE_FULL: limit=${error.limit}`, context: 'CarForm.upload' }).catch(() => {});
        }
      } else if ((error as any).code === '23514') {
        errorMessage = t.common.mandatoryFieldsError;
      }
      setAlertConfig({ isOpen: true, title: t.common.error, message: errorMessage });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = initialValues ? (
    name !== initialValues.name ||
    make !== initialValues.make ||
    model !== initialValues.model ||
    year !== initialValues.year ||
    plateNumber !== initialValues.plateNumber ||
    vinNumber !== initialValues.vinNumber ||
    isPublic !== initialValues.isPublic ||
    shareLocations !== initialValues.shareLocations ||
    fuelType !== initialValues.fuelType ||
    placeholderColor !== initialValues.placeholderColor ||
    useImageIcon !== initialValues.useImageIcon ||
    iconText !== initialValues.iconText ||
    (useImageIcon && iconPreviewUrl !== initialValues.image_url) ||
    (images.length !== (initialValues.gallery_count || 0)) ||
    // For new cars, we ignore unit preference changes unless the user has entered some main data
    ((editingCar || (name || make || model)) && (
      distanceUnit !== initialValues.distanceUnit ||
      volumeUnit !== initialValues.volumeUnit ||
      currency !== initialValues.currency
    )) ||
    boughtAt !== initialValues.boughtAt ||
    purchasePrice !== (initialValues.purchasePrice || '') ||
    purchaseMileage !== (initialValues.purchaseMileage || '') ||
    JSON.stringify(carExpenseTypes) !== JSON.stringify(initialValues.expenseTypes) ||
    JSON.stringify(allowedEmails) !== JSON.stringify(initialValues.allowedEmails) ||
    bodyType !== (initialValues.bodyType || 'not_specified') ||
    (bodyType === 'other' && customBodyType !== initialValues.customBodyType) ||
    drivetrain !== (initialValues.drivetrain || 'not_specified') ||
    transmission !== (initialValues.transmission || 'not_specified') ||
    engineInfo !== (initialValues.engineInfo || '')
  ) : (
    // No initialValues yet, check for any entered data
    name.trim() !== '' ||
    make.trim() !== '' ||
    model.trim() !== '' ||
    plateNumber.trim() !== '' ||
    vinNumber.trim() !== '' ||
    boughtAt !== '' ||
    purchasePrice !== '' ||
    purchaseMileage !== '' ||
    engineInfo.trim() !== ''
  );

  useEffect(() => {
    if (hasChanges && initialValues) {
      console.log('--- CarForm Changes Detected ---');
      if (name !== initialValues.name) console.log('name:', name, '!==', initialValues.name);
      if (make !== initialValues.make) console.log('make:', make, '!==', initialValues.make);
      if (model !== initialValues.model) console.log('model:', model, '!==', initialValues.model);
      if (year !== initialValues.year) console.log('year:', year, '!==', initialValues.year);
      if (plateNumber !== initialValues.plateNumber) console.log('plateNumber:', plateNumber, '!==', initialValues.plateNumber);
      if (vinNumber !== initialValues.vinNumber) console.log('vinNumber:', vinNumber, '!==', initialValues.vinNumber);
      if (isPublic !== initialValues.isPublic) console.log('isPublic:', isPublic, '!==', initialValues.isPublic);
      if (fuelType !== initialValues.fuelType) console.log('fuelType:', fuelType, '!==', initialValues.fuelType);
      if (placeholderColor !== initialValues.placeholderColor) console.log('placeholderColor:', placeholderColor, '!==', initialValues.placeholderColor);
      if (useImageIcon !== initialValues.useImageIcon) console.log('useImageIcon:', useImageIcon, '!==', initialValues.useImageIcon);
      if (iconText !== initialValues.iconText) console.log('iconText:', iconText, '!==', initialValues.iconText);
      if (useImageIcon && iconPreviewUrl !== initialValues.image_url) console.log('iconPreviewUrl:', iconPreviewUrl, '!==', initialValues.image_url);
      if (images.length !== (initialValues.gallery_count || 0)) console.log('gallery_count:', images.length, '!==', initialValues.gallery_count);
      if (JSON.stringify(carExpenseTypes) !== JSON.stringify(initialValues.expenseTypes)) console.log('expenseTypes change');
      if (JSON.stringify(allowedEmails) !== JSON.stringify(initialValues.allowedEmails)) console.log('allowedEmails change');
      if (bodyType !== (initialValues.bodyType || 'not_specified')) console.log('bodyType:', bodyType, '!==', (initialValues.bodyType || 'not_specified'));
      if (bodyType === 'other' && customBodyType !== initialValues.customBodyType) console.log('customBodyType:', customBodyType, '!==', initialValues.customBodyType);
      if (drivetrain !== (initialValues.drivetrain || 'not_specified')) console.log('drivetrain:', drivetrain, '!==', (initialValues.drivetrain || 'not_specified'));
      if (transmission !== (initialValues.transmission || 'not_specified')) console.log('transmission:', transmission, '!==', (initialValues.transmission || 'not_specified'));
      if (engineInfo !== (initialValues.engineInfo || '')) console.log('engineInfo:', engineInfo, '!==', (initialValues.engineInfo || ''));
      if (boughtAt !== initialValues.boughtAt) console.log('boughtAt:', boughtAt, '!==', initialValues.boughtAt);
      if (purchasePrice !== (initialValues.purchasePrice || '')) console.log('purchasePrice:', purchasePrice, '!==', (initialValues.purchasePrice || ''));
      if (purchaseMileage !== (initialValues.purchaseMileage || '')) console.log('purchaseMileage:', purchaseMileage, '!==', (initialValues.purchaseMileage || ''));
    }
  }, [hasChanges, initialValues, name, make, model, year, plateNumber, vinNumber, isPublic, fuelType, placeholderColor, useImageIcon, iconText, iconPreviewUrl, images, carExpenseTypes, allowedEmails, bodyType, customBodyType, drivetrain, transmission, engineInfo, boughtAt, purchasePrice, purchaseMileage]);

  const handleBack = () => {
    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      onBack();
    }
  };

  const isReadOnly = !!(editingCar?.is_archived) && !isAdminView;
  const isOwner = editingCar?.user_id === user?.id || (isAdmin && isAdminView) || outletContext.isOwner;

  const showSaveCancelButtons = !isReadOnly && (!editingCar || hasChanges);

  return (
    <div className="fixed inset-0 flex flex-col h-full overflow-hidden bg-gradient-theme">
      {showConfetti && <Confetti />}
      <NavigationBar
        title={editingCar ? t.cars.editCar : t.cars.newCar}
        subtitle={editingCar ? `${editingCar.make} ${editingCar.model} ` : undefined}
        onBack={handleBack}
        fixed={false}
        className="bg-transparent"
        rightAction={showSaveCancelButtons ? {
          icon: saving ? Loader2 : Save,
          onClick: () => handleSubmit(),
          disabled: saving,
          className: saving ? 'animate-spin' : ''
        } : undefined}
      />

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto pt-6 pb-20 no-scrollbar"
      >
        <div className="max-w-2xl mx-auto w-full">
          <div className={`${theme.spacing.pagePadding} `}>
            {/* Icon Picker Header */}
            <div className={`relative mb-8 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
              <div
                onClick={() => setIsIconPickerOpen(true)}
                className={`${theme.radius.card} overflow-hidden ${theme.colors.border} border ${theme.effects.shadow} relative cursor-pointer group`}
              >
                {/* Car Image or Gradient Background */}
                {useImageIcon && iconPreviewUrl ? (
                  <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={iconPreviewUrl}
                      alt={name || `${make} ${model}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 from-0% via-black/20 via-40% to-transparent to-70% z-0" />
                  </div>
                ) : (
                  <div
                    className="relative w-full aspect-[4/3]"
                    style={{
                      background: placeholderColor || '#3B82F6',
                      backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.2) 100%)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 from-0% via-black/20 via-40% to-transparent to-70% z-0" />
                    <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                      <span className="text-8xl drop-shadow-xl">{iconText || '🚗'}</span>
                    </div>
                  </div>
                )}

                {/* Car Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 text-white flex items-end justify-between pointer-events-none z-20">
                  <div className="pointer-events-auto">
                    <div className="flex items-center gap-3 mb-2">
                      {brandLogoUrl && (
                        <img
                          src={brandLogoUrl}
                          alt={make}
                          className="w-8 h-8 object-contain bg-white/90 rounded-lg p-1"
                        />
                      )}
                      <h1 className="text-lg md:text-2xl font-black">
                        {name || `${make} ${model}`.trim() || t.cars.newCar}
                      </h1>
                      {isReadOnly && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/80 text-white text-xs font-bold">
                          <Archive size={11} />
                          {(t.cars as any).archivedBadge || 'Archived'}
                        </span>
                      )}
                    </div>
                    <p className="text-xs md:text-sm font-medium text-white/80">
                      {year} • {make} {model}
                    </p>
                  </div>


                  {/* Edit Badge matching CarOverview */}
                  {!isReadOnly && (
                    <div className="mb-1 p-2.5 bg-blue-600 dark:bg-blue-500 rounded-full border-2 border-blue-400/30 shadow-lg shadow-blue-500/20 group-hover:bg-blue-700 dark:group-hover:bg-blue-400 transition-all group-active:scale-95 flex items-center justify-center pointer-events-auto">
                      <Edit size={20} strokeWidth={2.5} className="text-white" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <form id="car-form" onSubmit={handleSubmit} className="space-y-6">
              {/* Read-only notice for archived cars */}
              {isReadOnly && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-400/20">
                  <Archive size={20} className="text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200 leading-relaxed">
                    {(t.cars as any).archiveInstructions || 'This car is archived and read-only.'}
                  </p>
                </div>
              )}
              {/* Section Header: Identity */}
              <div className={`flex items-center justify-between mb-4 px-1 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <h2 className={theme.typography.sectionTitle}>{t.cars.vehicleInfo}</h2>
              </div>

              {/* Group 1: Identity */}
              <div className={`px-2 space-y-6 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <div>
                  <FormLabel className={theme.typography.sectionLabel} isFilled={!!name}>
                    {t.cars.displayName}
                  </FormLabel>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                    placeholder={t.cars.displayNamePlaceholder}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="relative">
                    <FormLabel isFilled={!!make}>
                      {t.cars.makeLabel}
                    </FormLabel>
                    <input
                      ref={makeInputRef}
                      type="text"
                      value={make}
                      onChange={(e) => {
                        const value = e.target.value;
                        setMake(value);
                        if (value.length > 0) {
                          const matches = carBrandsData.brands.filter(brand =>
                            brand.name.toLowerCase().includes(value.toLowerCase())
                          ).slice(0, 8);
                          setFilteredBrands(matches);
                        } else {
                          setFilteredBrands([]);
                        }
                      }}
                      onFocus={() => {
                        setMakeInputFocused(true);
                        if (make.length > 0) {
                          const matches = carBrandsData.brands.filter(brand =>
                            brand.name.toLowerCase().includes(make.toLowerCase())
                          ).slice(0, 8);
                          setFilteredBrands(matches);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setMakeInputFocused(false), 200);
                      }}
                      required
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                      placeholder={t.cars.makePlaceholder}
                    />

                    {makeInputFocused && filteredBrands.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                        {filteredBrands.map((brand) => (
                          <button
                            key={brand.name}
                            type="button"
                            onClick={() => {
                              setMake(brand.name);
                              setBrandLogoUrl(brand.logo || null);
                              setFilteredBrands([]);
                              makeInputRef.current?.blur();
                            }}
                            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-left border-b border-gray-100 dark:border-white/5 last:border-0"
                          >
                            {brand.logo && (
                              <img
                                src={brand.logo}
                                alt={brand.name}
                                className="w-8 h-8 object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {brand.name}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <FormLabel isFilled={!!model}>
                      {t.cars.modelLabel}
                    </FormLabel>
                    <input
                      ref={modelInputRef}
                      type="text"
                      value={model}
                      onChange={(e) => {
                        const value = e.target.value;
                        setModel(value);
                        const currentBrand = carBrandsData.brands.find(b => b.name.toLowerCase() === make.toLowerCase());
                        if (currentBrand && value.length > 0) {
                          const matches = currentBrand.models.filter(m =>
                            m.toLowerCase().includes(value.toLowerCase())
                          ).slice(0, 8);
                          setFilteredModels(matches);
                        } else {
                          setFilteredModels([]);
                        }
                      }}
                      onFocus={() => {
                        setModelInputFocused(true);
                        const currentBrand = carBrandsData.brands.find(b => b.name.toLowerCase() === make.toLowerCase());
                        if (currentBrand) {
                          const matches = currentBrand.models.filter(m =>
                            m.toLowerCase().includes(model.toLowerCase())
                          ).slice(0, 8);
                          setFilteredModels(matches);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setModelInputFocused(false), 200);
                      }}
                      required
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                      placeholder={t.cars.modelPlaceholder}
                    />

                    {modelInputFocused && filteredModels.length > 0 && (
                      <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                        {filteredModels.map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              setModel(m);
                              setFilteredModels([]);
                              modelInputRef.current?.blur();
                            }}
                            className="w-full px-4 py-3 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors text-left border-b border-gray-100 dark:border-white/5 last:border-0"
                          >
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {m}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FormLabel isFilled={!!year}>
                      {t.cars.yearLabel}
                    </FormLabel>
                    <input
                      type="number"
                      value={year}
                      onChange={(e) => setYear(parseInt(e.target.value))}
                      required
                      min="1900"
                      max={new Date().getFullYear() + 1}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                    />
                  </div>
                  <div>
                    <FormLabel isFilled={!!fuelType && fuelType !== 'not_specified'}>
                      {t.cars.fuelTypeLabel}
                    </FormLabel>
                    <select
                      value={fuelType}
                      onChange={(e) => {
                        const newType = e.target.value as FuelType;
                        setFuelType(newType);

                        // Auto-adjust expense types based on fuel type
                        let newExpenseTypes = [...carExpenseTypes];
                        if (['petrol', 'diesel', 'lpg'].includes(newType)) {
                          if (!newExpenseTypes.includes('fuel')) newExpenseTypes.push('fuel');
                          newExpenseTypes = newExpenseTypes.filter(t => t !== 'electricity');
                        } else if (newType === 'hybrid') {
                          if (!newExpenseTypes.includes('fuel')) newExpenseTypes.push('fuel');
                          if (!newExpenseTypes.includes('electricity')) newExpenseTypes.push('electricity');
                        } else if (newType === 'electric') {
                          if (!newExpenseTypes.includes('electricity')) newExpenseTypes.push('electricity');
                          newExpenseTypes = newExpenseTypes.filter(t => t !== 'fuel');
                        }
                        setCarExpenseTypes(newExpenseTypes);
                      }}
                      required
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      {Object.entries(t.fuelTypes).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FormLabel>
                      {`${t.carMetadata.bodyType}${bodyType === 'other' ? ' *' : ''}`}
                    </FormLabel>
                    <div className="space-y-3">
                      <select
                        value={bodyType}
                        onChange={(e) => setBodyType(e.target.value)}
                        className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                      >
                        {Object.entries(t.carMetadata.bodyTypes).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                      
                      {bodyType === 'other' && (
                        <input
                          type="text"
                          value={customBodyType}
                          onChange={(e) => setCustomBodyType(e.target.value)}
                          placeholder={t.carMetadata.otherLabel}
                          required
                          className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white"
                        />
                      )}
                    </div>
                  </div>
                  <div>
                    <FormLabel>
                      {t.carMetadata.drivetrain}
                    </FormLabel>
                    <select
                      value={drivetrain}
                      onChange={(e) => setDrivetrain(e.target.value)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      {Object.entries(t.carMetadata.drivetrains).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <FormLabel>
                    {t.carMetadata.transmission}
                  </FormLabel>
                  <select
                    value={transmission}
                    onChange={(e) => setTransmission(e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                  >
                    {Object.entries(t.carMetadata.transmissions).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                    <FormLabel>
                      {t.cars.notesLabel}
                    </FormLabel>
                  <input
                    type="text"
                    value={engineInfo}
                    onChange={(e) => setEngineInfo(e.target.value)}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                    placeholder={t.cars.notesPlaceholder}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FormLabel>
                      {t.cars.licensePlateLabel}
                    </FormLabel>
                    <input
                      type="text"
                      value={plateNumber}
                      onChange={(e) => setPlateNumber(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                      placeholder={t.cars.platePlaceholder}
                    />
                  </div>
                  <div>
                    <FormLabel>
                      {t.cars.vinLabel}
                    </FormLabel>
                    <input
                      type="text"
                      value={vinNumber}
                      onChange={(e) => setVinNumber(e.target.value.toUpperCase())}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-white/20"
                      placeholder={t.cars.vinPlaceholder}
                    />
                  </div>
                </div>
              </div>

              {/* Section Header: Preferences */}
              <div className={`flex items-center justify-between mt-4 mb-4 px-1 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <h2 className={theme.typography.sectionTitle}>{t.cars.preferences}</h2>
              </div>

              {/* Group 2: Preferences */}
              <div className={`px-2 space-y-6 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <FormLabel>
                      {t.cars.currencyLabel || t.settings.currency}
                    </FormLabel>
                    <select
                      value={currency}
                      onChange={(e) => handleUnitChange(setCurrency, e.target.value as Currency, currency)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      {Object.keys(CURRENCY_SYMBOLS).map((c) => (
                        <option key={c} value={c}>
                          {c} ({CURRENCY_SYMBOLS[c as Currency]})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FormLabel>
                      {t.cars.distanceUnitLabel || t.settings.distanceUnit}
                    </FormLabel>
                    <select
                      value={distanceUnit}
                      onChange={(e) => handleUnitChange(setDistanceUnit, e.target.value as 'km' | 'miles', distanceUnit)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      <option value="km">{t.settings.kilometers}</option>
                      <option value="miles">{t.settings.miles}</option>
                    </select>
                  </div>
                  <div>
                    <FormLabel>
                      {t.cars.volumeUnitLabel || t.settings.volumeUnit}
                    </FormLabel>
                    <select
                      value={volumeUnit}
                      onChange={(e) => handleUnitChange(setVolumeUnit, e.target.value as 'liters' | 'gallons', volumeUnit)}
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                      <option value="liters">{t.settings.liters}</option>
                      <option value="gallons">{t.settings.gallons}</option>
                    </select>
                  </div>
                </div>

                {/* Hidden for now as per user request */}
                {/* <div>
                    <FormLabel>
                        {(t.cars as any).summaryFrequencyLabel || 'Summary Frequency'}
                    </FormLabel>
                    <select
                        value={summaryFrequency}
                        onChange={(e) => setSummaryFrequency(e.target.value)}
                        className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white appearance-none"
                    >
                        {Object.entries((t.cars as any).summaryFrequencyOptions || {}).map(([value, label]) => (
                            <option key={value} value={value}>
                                {label as string}
                            </option>
                        ))}
                    </select>
                </div> */}
              </div>

              {/* Section Header: Purchase Details */}
              <div className={`flex items-center justify-between mt-4 mb-4 px-1 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <h2 className={theme.typography.sectionTitle}>{t.cars.purchaseHistory}</h2>
              </div>

              {/* Group 3: Purchase Details */}
              <div className={`px-2 space-y-6 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <DateInput
                    label={t.cars.boughtAt}
                    value={boughtAt}
                    onChange={setBoughtAt}
                    required
                    language={language}
                    placeholder={t.cars.enterPurchaseDateAlert}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <FormLabel>
                      {t.cars.purchasePrice} ({CURRENCY_SYMBOLS[currency]})
                    </FormLabel>
                    <input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      value={purchasePrice}
                      onChange={(e) => setPurchasePrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <FormLabel>
                      {t.cars.purchaseMileage} ({distanceUnit === 'km' ? t.common.units.kilometers : t.common.units.miles})
                    </FormLabel>
                    <input
                      id="purchase_mileage"
                      type="number"
                      value={purchaseMileage}
                      onChange={(e) => setPurchaseMileage(e.target.value)}
                      placeholder="0"
                      className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-2xl border border-blue-100 dark:border-blue-400/10">
                  <History size={20} className="text-blue-600 dark:text-blue-400 mt-1 flex-shrink-0" />
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200 leading-relaxed">
                    {t.cars.purchaseInfoNote}
                  </p>
                </div>
              </div>

              {/* Sell Details Section — visible only when archived */}
              {isReadOnly && (
                <>
                  <div className="flex items-center justify-between mt-4 mb-4 px-1 pointer-events-auto">
                    <h2 className={theme.typography.sectionTitle}>{(t.cars as any).saleDetails || 'Sale Details'}</h2>
                  </div>
                  <div className="px-2 space-y-6 pointer-events-auto" style={{ opacity: 1 }}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <DateInput
                        label={(t.cars as any).sellDate || 'Sell Date'}
                        value={sellDate}
                        onChange={setSellDate}
                        required
                        language={language}
                        disabled={false}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <FormLabel className={theme.typography.sectionLabel} isFilled={!!sellPrice}>
                          {`${(t.cars as any).sellPrice || 'Sell Price'} (${CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency}) *`}
                        </FormLabel>
                        <input
                          type="number"
                          step="0.01"
                          value={sellPrice}
                          onChange={(e) => setSellPrice(e.target.value)}
                          placeholder="0.00"
                          className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white"
                          style={{ pointerEvents: 'all' }}
                        />
                      </div>
                      <div>
                        <FormLabel className={theme.typography.sectionLabel} isFilled={!!sellMileage}>
                          {`${(t.cars as any).sellMileage || 'Sell Mileage'} (${distanceUnit === 'km' ? t.common.units.kilometers : t.common.units.miles}) *`}
                        </FormLabel>
                        <input
                          type="number"
                          value={sellMileage}
                          onChange={(e) => setSellMileage(e.target.value)}
                          placeholder="0"
                          className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white"
                          style={{ pointerEvents: 'all' }}
                        />
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-400/20">
                      <Archive size={20} className="text-amber-600 dark:text-amber-400 mt-1 flex-shrink-0" />
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-200 leading-relaxed">
                        {(t.cars as any).saleDetailsNote || 'Sale details are used for statistics. You can update them if needed.'}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveSellDetails}
                      disabled={savingSellDetails}
                      style={{ pointerEvents: 'all' }}
                      className="w-full py-3.5 px-6 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {savingSellDetails ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      {t.common.save}
                    </button>
                  </div>
                </>
              )}

              {/* Combined Email Processing Section (only for edit) */}
              {editingCar && (
                <>
                  <div className={`flex items-center justify-between mt-4 mb-4 px-1 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                    <h2 className={theme.typography.sectionTitle}>{t.cars.emailProcessingTitle}</h2>
                  </div>
                  <div className={`px-2 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                    <EmailProcessingCombined
                      car={editingCar}
                      ownerEmail={user?.email}
                      allowedEmails={allowedEmails}
                      onAllowedEmailsChange={setAllowedEmails}
                      isAdminView={isAdminView}
                      isPro={isPro}
                    />
                  </div>
                </>
              )}


              {/* Section Header: Expense Types */}
              <div className={`flex items-center justify-between mt-4 mb-4 px-1 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <h2 className={theme.typography.sectionTitle}>{t.cars.expenseTypesTitle}</h2>
              </div>

              {/* Group: Expense Types */}
              <div className={`px-2 ${isReadOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl overflow-hidden shadow-sm p-4">
                  <div className="mt-0">
                    <CarExpenseTypesEditor
                      selectedTypes={carExpenseTypes}
                      onChange={setCarExpenseTypes}
                    />
                    <div className="pt-2 text-center mt-4">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t.cars.requestTypeNote}
                        <Link
                          to="/contact"
                          className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
                        >
                          {t.cars.requestTypeAction}
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Header: Car Administrators */}
              {editingCar && (
                <div className={isReadOnly ? 'opacity-60 pointer-events-none' : ''}>
                  <div className="flex items-center justify-between mt-4 mb-4 px-1">
                    <h2 className={theme.typography.sectionTitle}>{t.cars.carAdmins || 'Car Administrators'}</h2>
                  </div>
                  <div className="px-2">
                    <CarAdminsSection
                      carId={editingCar.id}
                      isOwner={isOwner}
                      isAdminView={isAdminView}
                      isPro={isPro}
                      onRemovingAccess={() => {
                        if (onCarDeleted) onCarDeleted();
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Advanced Section - Contains Public Switch and Delete */}
              <div className="flex items-center justify-between mt-4 mb-4 px-1">
                <h2 className={theme.typography.sectionTitle}>{t.common.additionalOptions}</h2>
              </div>
              <div className="px-2 space-y-4">
                <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between p-4 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center">
                        <Eye size={20} />
                      </div>
                      <div>
                        <span className="text-base font-medium text-gray-900 dark:text-white">
                          {t.cars.publicAccess}
                        </span>
                        <p className="text-xs font-normal text-gray-400 dark:text-white/40">{t.cars.publicAccessDesc}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isPublic}
                      onClick={() => {
                        const newPublic = !isPublic;
                        setIsPublic(newPublic);
                        if (!newPublic) setShareLocations(false);
                      }}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPublic ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPublic ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>

                  {isPublic && (
                    <div className="flex items-center justify-between p-4 border-t border-gray-100 dark:border-white/10 transition-all animate-in slide-in-from-top-2 duration-200">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
                          <MapPin size={20} />
                        </div>
                        <div>
                          <span className="text-base font-medium text-gray-900 dark:text-white">
                            {(t.cars as any).shareLocationsLabel || 'Allow to view also added locations'}
                          </span>
                          <p className="text-xs font-normal text-gray-400 dark:text-white/40">
                            {(t.cars as any).shareLocationsDesc || 'Enable this to make car locations visible in public view'}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={shareLocations}
                        onClick={() => setShareLocations(!shareLocations)}
                        className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${shareLocations ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700'}`}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${shareLocations ? 'translate-x-5' : 'translate-x-0'}`}
                        />
                      </button>
                    </div>
                  )}
                </div>

                {/* Export Button */}
                {editingCar && (
                  <div className="px-0 pt-4">
                      <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden pointer-events-auto" style={{ pointerEvents: 'all' }}>
                        <button
                          type="button"
                          onClick={() => navigate(`/${editingCar.slug}/export`)}
                          className="w-full text-left p-4 text-base font-bold text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-all flex items-center group"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center">
                              <FileDown size={20} />
                            </div>
                            <span>{(t.export as any).exportButton || 'Export Data'}</span>
                          </div>
                        </button>
                      </div>
                  </div>
                )}

                {/* Archive Car button (only for owner/admin, only when not archived) */}
                {editingCar && !isReadOnly && isOwner && (
                  <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden pointer-events-auto" style={{ pointerEvents: 'all' }}>
                    <button
                      type="button"
                      onClick={() => setShowArchiveModal(true)}
                      className="w-full text-left p-4 text-base font-bold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors flex items-center group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center">
                          <Archive size={20} />
                        </div>
                        <span>{(t.cars as any).archiveCar || 'Archive Car'}</span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Unarchive button — admin view only */}
                {editingCar && isAdminView && isAdmin && (
                  <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden pointer-events-auto">
                    <button
                      type="button"
                      onClick={handleUnarchive}
                      disabled={archiving}
                      className="w-full text-left p-4 text-base font-bold text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/10 transition-all flex items-center group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center">
                          {archiving ? <Loader2 size={20} className="animate-spin" /> : <ArchiveX size={20} />}
                        </div>
                        <span>{(t.cars as any).unarchiveCar || 'Unarchive Car'}</span>
                      </div>
                    </button>
                  </div>
                )}

                {/* Show Delete Button only if editing existing car (Owner only) */}
                {editingCar && isOwner && (
                  <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm overflow-hidden pointer-events-auto" style={{ pointerEvents: 'all' }}>
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="w-full text-left p-4 text-base font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors flex items-center group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center">
                          <Trash2 size={20} />
                        </div>
                        <span>{t.cars.deleteCar || 'Delete Car'}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {editingCar && isAdmin && (
                <div className={isReadOnly ? 'opacity-60 pointer-events-none' : ''}>
                  {/* Section Header: Admin Actions */}
                  <div className="flex items-center justify-between mt-8 mb-4 px-1">
                    <h2 className={theme.typography.sectionTitle}>{t.cars.adminActionsTitle}</h2>
                  </div>

                  <div className="px-2">
                    <div className="bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/10 rounded-2xl shadow-sm divide-y divide-gray-100 dark:divide-white/5 overflow-hidden">
                      <button
                        type="button"
                        onClick={onImportClick}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50/80 dark:hover:bg-white/5 transition-all active:scale-[0.99]"
                      >
                        <div className={`flex items - center ${theme.spacing.itemGap} `}>
                          <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center transition-colors shadow-sm">
                            <Upload size={24} />
                          </div>
                          <div className="text-left">
                            <h3 className={theme.typography.itemTitle}>{t.cars.importCsv}</h3>
                            <p className={theme.typography.itemSubtitle}>Import expenses for this car</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-gray-300 transition-transform group-hover:translate-x-0.5" />
                      </button>

                      <button
                        type="button"
                        onClick={onDeleteAllClick}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50/80 dark:hover:bg-white/5 transition-all active:scale-[0.99] group"
                      >
                        <div className={`flex items - center ${theme.spacing.itemGap} `}>
                          <div className="w-10 h-10 bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center transition-colors shadow-sm">
                            <History size={24} />
                          </div>
                          <div className="text-left">
                            <h3 className={`${theme.typography.itemTitle} text - red - 600`}>{t.cars.clearData}</h3>
                            <p className={theme.typography.itemSubtitle}>Remove all expenses for this car</p>
                          </div>
                        </div>
                        <ChevronRight size={20} className="text-red-500 opacity-30 transition-transform group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <CarIconPicker
        isOpen={isIconPickerOpen}
        onClose={() => setIsIconPickerOpen(false)}
        onSave={handleIconSave}
        initialColor={placeholderColor}
        initialEmoji={iconText}
        initialImage={iconPreviewUrl || undefined}
      />

      <ConfirmModal
        isOpen={showConfirmModal}
        title={t.common.unsavedChangesTitle}
        message={t.common.unsavedChangesMessage}
        confirmLabel={t.common.save}
        cancelLabel={t.common.dontSave}
        onConfirm={async () => {
          const success = await handleSubmit();
          if (success) {
            setShowConfirmModal(false);
            onBack();
          }
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          onBack();
        }}
        onClose={() => setShowConfirmModal(false)}
        isDestructive={false}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title={t.cars.deleteCarTitle}
        message={t.cars.deleteCarConfirm.replace('{name}', editingCar?.name || (editingCar ? `${editingCar.make} ${editingCar.model}` : 'this car'))}
        confirmLabel={t.common.delete}
        cancelLabel={t.common.cancel}
        onConfirm={handleDeleteCall}
        onCancel={() => setShowDeleteModal(false)}
        onClose={() => setShowDeleteModal(false)}
        isDestructive={true}
      />

      {/* Archive Modal */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowArchiveModal(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md p-6 space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/40 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Archive size={24} className="text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="text-xl font-black text-gray-900 dark:text-white">{(t.cars as any).archiveCar || 'Archive Car'}</h2>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
              {(t.cars as any).archiveInstructions || 'Archiving marks this car as sold. Please enter sale details.'}
            </p>

            <div className="space-y-4">
              <div>
                <label className={theme.typography.sectionLabel}>{(t.cars as any).sellDate || 'Sell Date'} *</label>
                <input
                  type="date"
                  value={sellDate}
                  onChange={(e) => setSellDate(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white mt-1"
                />
              </div>
              <div>
                <label className={theme.typography.sectionLabel}>{(t.cars as any).sellPrice || 'Sell Price'} ({CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={sellPrice}
                  onChange={(e) => setSellPrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white mt-1"
                />
              </div>
              <div>
                <label className={theme.typography.sectionLabel}>{(t.cars as any).sellMileage || 'Sell Mileage'} ({distanceUnit === 'km' ? t.common.units.kilometers : t.common.units.miles}) *</label>
                <input
                  type="number"
                  value={sellMileage}
                  onChange={(e) => setSellMileage(e.target.value)}
                  placeholder="0"
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all font-bold text-gray-900 dark:text-white mt-1"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowArchiveModal(false)}
                className="flex-1 py-3 px-4 rounded-2xl border border-gray-200 dark:border-white/10 text-gray-700 dark:text-gray-300 font-bold hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
              >
                {t.common.cancel}
              </button>
              <button
                type="button"
                onClick={handleArchiveConfirm}
                disabled={archiving}
                className="flex-1 py-3 px-4 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {archiving ? <Loader2 size={18} className="animate-spin" /> : <Archive size={18} />}
                {(t.cars as any).archiveCar || 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}
      <AlertModal
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmLabel={t.common.done}
        onClose={() => setAlertConfig(prev => ({ ...prev, isOpen: false }))}
        secondaryAction={alertConfig.secondaryAction}
      />

      <Outlet />
    </div >
  );
}
